import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import { FaPlus, FaMinus } from 'react-icons/fa';
import LandingNavbar from './Navbar';
import LandingFooter from './LandingFooter';
import ScrollReveal from './ScrollReveal';
import { useDemoModal } from './DemoModal';
import {
  IND_HERO, WAVES, IND_WHAT, IND_PACK, IND_LIST, IND_FAQS, IND_CTA,
} from './industriesContent';
import './landing-animations.css';
import './landing-theme.css';
import './landing-eb.css';
import './landing-eb-industries.css';

// Brand icons: src/assets/images/eb/icons/<name>-<green|cream|gold>.png
const iconCtx = require.context('../../assets/images/eb/icons', false, /\.png$/);
const icon = (name, tone = 'green') => iconCtx(`./${name}-${tone}.png`);

const IndustriesPage = () => {
  const location = useLocation();
  const { openDemo: goDemo } = useDemoModal(); // "Book a demo" opens the demo form
  const [wave, setWave] = useState(0); // 0 = all eight
  const [openFaq, setOpenFaq] = useState(0);

  // Deep links such as /industries#oil-gas-energy
  useEffect(() => {
    if (!location.hash) return undefined;
    const t = window.setTimeout(() => {
      document.querySelector(location.hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    return () => window.clearTimeout(t);
  }, [location.hash]);

  const scrollTo = (hash) => document.querySelector(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const filters = [{ label: IND_LIST.allLabel }, ...WAVES];
  const shown = IND_LIST.items.filter((i) => wave === 0 || i.wave === wave);
  const sectionLabel = wave === 0 ? IND_LIST.allEyebrow : WAVES[wave - 1].label;
  const sectionTitle = wave === 0 ? IND_LIST.allTitle : WAVES[wave - 1].title;

  return (
    <div className="landing-page eb min-vh-100 d-flex flex-column">
      <ScrollReveal />
      <LandingNavbar />

      {/* ── Hero ── */}
      <header className="eb-ip-hero">
        <div className="eb-ip-hero-inner">
          <div className="eb-ip-hero-copy">
            <p className="eb-eyebrow mb-0">{IND_HERO.eyebrow}</p>
            <h1 className="eb-ip-h1">{IND_HERO.title}</h1>
            <p className="eb-lead mb-0">{IND_HERO.subtitle}</p>
            <div className="d-flex flex-wrap gap-3">
              <button type="button" className="landing-btn-primary" onClick={goDemo}>
                {IND_HERO.primaryCta}
              </button>
              <button type="button" className="landing-btn-secondary" onClick={() => scrollTo('#industries')}>
                {IND_HERO.secondaryCta}
              </button>
            </div>
          </div>
          <div className="eb-ip-waves">
            <span className="eb-ip-waves-label">{IND_HERO.wavesLabel}</span>
            {WAVES.map((w) => (
              <div key={w.n} className="eb-ip-wave">
                <span className="n">{w.n}</span>
                <div>
                  <strong>{w.title}</strong>
                  <span>{w.list}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </header>

      {/* ── What is workforce readiness ── */}
      <section className="eb-section eb-section-cream">
        <Container className="eb-ip-what">
          <div className="eb-ip-what-copy reveal reveal-up">
            <h2 className="eb-h2">{IND_WHAT.title}</h2>
            <p className="eb-ip-what-lead">{IND_WHAT.lead}</p>
            <p className="eb-lead mb-0">{IND_WHAT.text}</p>
          </div>
          <div className="eb-ip-changes reveal reveal-up">
            <span className="eb-ip-changes-label">{IND_WHAT.changesLabel}</span>
            {IND_WHAT.changes.map((c) => (
              <div key={c.k} className="eb-ip-change">
                <img src={icon(c.icon)} alt="" />
                <div><strong>{c.k}</strong><span>{c.d}</span></div>
              </div>
            ))}
            <p className="eb-ip-changes-close">{IND_WHAT.closing}</p>
          </div>
        </Container>
      </section>

      {/* ── What every pack adds ── */}
      <section className="eb-section">
        <Container className="eb-ip-pack">
          <div className="eb-ip-pack-head reveal reveal-up">
            <p className="eb-eyebrow mb-0">{IND_PACK.eyebrow}</p>
            <h2 className="eb-h2">{IND_PACK.title}</h2>
            <p className="eb-lead mb-0">
              {IND_PACK.textBefore}
              <Link to={IND_PACK.linkTo}>{IND_PACK.linkLabel}</Link>
              {IND_PACK.textAfter}
            </p>
          </div>
          <div className="eb-ip-pack-grid">
            {IND_PACK.items.map((p, i) => (
              <div key={p.k} className="eb-ip-pack-card reveal reveal-up">
                <span className="num">0{i + 1}</span>
                <strong>{p.k}</strong>
                <span className="d">{p.d}</span>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* ── Industries list with wave filter ── */}
      <section id="industries" className="eb-section eb-section-cream">
        <Container className="eb-ip-list">
          <div className="eb-ip-list-head">
            <div className="eb-ip-list-title">
              <p className="eb-eyebrow mb-0">{sectionLabel}</p>
              <h2 className="eb-h2">{sectionTitle}</h2>
            </div>
            <div className="eb-ip-filters" role="tablist" aria-label="Filter industries by wave">
              {filters.map((f, i) => (
                <button
                  key={f.label}
                  type="button"
                  role="tab"
                  aria-selected={wave === i}
                  className={`eb-ip-filter${wave === i ? ' is-on' : ''}`}
                  onClick={() => setWave(i)}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          <div className="eb-ip-cards">
            {shown.map((ind) => (
              <article key={ind.id} id={ind.id} className="eb-ip-card">
                <div className="eb-ip-card-photo">
                  {ind.photo ? (
                    <img src={ind.photo} alt="" />
                  ) : (
                    <div className="eb-ip-card-ph" aria-hidden="true"><img src={icon('image')} alt="" /></div>
                  )}
                  <span className="eb-ind-wave">WAVE {ind.wave}</span>
                </div>
                <div className="eb-ip-card-body">
                  <h3>{ind.name}</h3>
                  <p className="intro">{ind.intro}</p>
                  <ul className="eb-ip-points">
                    {ind.points.map((p) => (
                      <li key={p}><img src={icon('check')} alt="" /><span>{p}</span></li>
                    ))}
                  </ul>
                  <div className="eb-ip-outcome">
                    <strong>The outcome:</strong> {ind.outcome}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </Container>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" className="eb-section">
        <Container style={{ maxWidth: 900 }}>
          <h2 className="eb-h2 text-center mb-4 reveal reveal-up">Frequently asked questions</h2>
          <div className="eb-ip-faq-list">
            {IND_FAQS.map(({ q, a }, i) => {
              const open = openFaq === i;
              return (
                <div key={q} className="eb-faq-item">
                  <button
                    type="button"
                    className="eb-faq-q"
                    aria-expanded={open}
                    aria-controls={`eb-ip-faq-${i}`}
                    onClick={() => setOpenFaq(open ? -1 : i)}
                  >
                    {q}
                    <span className="eb-faq-sign">{open ? <FaMinus size={12} /> : <FaPlus size={12} />}</span>
                  </button>
                  {open && <div id={`eb-ip-faq-${i}`} className="eb-faq-a">{a}</div>}
                </div>
              );
            })}
          </div>
        </Container>
      </section>

      {/* ── Demo CTA ── */}
      <section id="demo" className="eb-section">
        <Container>
          <div className="eb-cta-card text-center reveal reveal-up">
            <h2 className="eb-h2">{IND_CTA.title}</h2>
            <p className="eb-lead">{IND_CTA.text}</p>
            <button type="button" className="landing-btn-primary" onClick={goDemo}>
              {IND_CTA.button}
            </button>
          </div>
        </Container>
      </section>

      <LandingFooter />
    </div>
  );
};

export default IndustriesPage;